import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { ENV_CONFIG } from '../config/env';
import { getApiTimeouts } from '../utils/apiTimeouts';
import { ROUTES } from '../constants/routes';

// Usar la configuración centralizada de entorno
export const API_BASE_URL = `${ENV_CONFIG.API_BASE_URL}/api/v1`;

/** Rutas que no deben forzar token ni redirigir a login en 401 (fragmentos de URL bajo /api/v1). */
const PUBLIC_API_PATH_FRAGMENTS = [
  '/auth/login',
  '/auth/register',
  '/auth/reactivate',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/send-verification-code',
  '/auth/verify-email',
  '/auth/verify-email-token',
  '/auth/request-reactivation',
  '/subscriptions/extend-trial',
  '/pets/public',
  '/files/public',
  '/legal/terms-and-conditions',
  '/system/settings/public',
  '/privacy/compliance-info',
  '/privacy/consent/cookies',
] as const;

const isPublicApiPath = (url?: string) =>
  PUBLIC_API_PATH_FRAGMENTS.some((fragment) => url?.includes(fragment));

export interface AuthTokensEventDetail {
  accessToken: string | null;
  refreshToken: string | null;
}

export const AUTH_TOKENS_EVENT = 'auth:tokens';

declare global {
  interface WindowEventMap {
    'auth:tokens': CustomEvent<AuthTokensEventDetail>;
  }
}

export const emitAuthTokensEvent = (accessToken: string | null, refreshToken: string | null) => {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') {
    return;
  }

  const event = new CustomEvent<AuthTokensEventDetail>(AUTH_TOKENS_EVENT, {
    detail: {
      accessToken,
      refreshToken
    }
  });
  window.dispatchEvent(event);
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000, // Valor inicial, se actualizará dinámicamente
});

// Función helper para verificar si el token está próximo a expirar
const isTokenExpiringSoon = (token: string | null, thresholdMinutes: number = 5): boolean => {
  if (!token) return false;
  
  try {
    const decoded = jwtDecode<{ exp?: number }>(token);
    if (!decoded.exp) return false;
    
    const expirationTime = decoded.exp * 1000; // Convertir a milisegundos
    const timeUntilExpiry = expirationTime - Date.now();
    const thresholdMs = thresholdMinutes * 60 * 1000;
    
    return timeUntilExpiry > 0 && timeUntilExpiry < thresholdMs;
  } catch {
    return false;
  }
};

const proactiveRefresh = async (): Promise<boolean> => {
  try {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return false;

    const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
      refresh_token: refreshToken,
    });

    const { access_token, refresh_token } = response.data;
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    emitAuthTokensEvent(access_token, refresh_token);

    return true;
  } catch {
    return false;
  }
};

const clearSessionAndRedirect = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  emitAuthTokensEvent(null, null);
  window.location.href = ROUTES.LOGIN;
};

api.interceptors.request.use(async (config) => {
  const token = localStorage.getItem('access_token');
  
  // Si no tiene timeout específico, usar el por defecto del sistema
  if (!config.timeout) {
    try {
      const timeouts = await getApiTimeouts();
      config.timeout = timeouts.default;
    } catch (error) {
      // Si falla, usar valor por defecto
      config.timeout = 60000;
    }
  }
  
  // Endpoints públicos que no requieren token de autorización
  const isPublicEndpoint = isPublicApiPath(config.url);
  
  // Solo agregar token de autorización si NO es un endpoint público
  if (!isPublicEndpoint) {
    // Refresh proactivo si el token está próximo a expirar (15 minutos antes)
    if (token && isTokenExpiringSoon(token, 15)) {
      await proactiveRefresh();
      // Actualizar token después del refresh
      const newToken = localStorage.getItem('access_token');
      if (newToken) {
        config.headers.Authorization = `Bearer ${newToken}`;
      }
    } else if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  
  return config;
});

api.interceptors.response.use(
  (response) => {
    // Verificar si el token está próximo a expirar después de una respuesta exitosa (15 minutos antes)
    const token = localStorage.getItem('access_token');
    if (token && isTokenExpiringSoon(token, 15)) {
      // Hacer refresh proactivo en background (no bloquear la respuesta)
      proactiveRefresh().catch((error) => {
        console.warn('Error en refresh proactivo:', error);
      });
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Si la respuesta no es JSON (por ejemplo, HTML de error), crear un error más limpio
    if (error.response && typeof error.response.data === 'string' && error.response.data.trim().startsWith('<!')) {
      // Es HTML, no JSON - crear un error más limpio
      const status = error.response.status;
      const statusText = error.response.statusText || 'Error';
      error.response.data = {
        detail: status === 404 
          ? 'Recurso no encontrado' 
          : status === 403 
          ? 'Acceso denegado' 
          : `Error ${status}: ${statusText}`
      };
    }

    // Normalizar errores 422 de validación de Pydantic para evitar que se pasen como children a React
    if (error.response?.status === 422 && error.response.data) {
      const errorData = error.response.data;
      
      // Si es un array de errores de validación (estructura típica de Pydantic)
      if (Array.isArray(errorData.detail)) {
        const validationMessages = errorData.detail
          .map((err: any) => {
            if (typeof err === 'string') return err;
            if (err.msg) return err.msg;
            if (err.loc && err.msg) return `${err.loc.join('.')}: ${err.msg}`;
            return 'Error de validación';
          })
          .join(', ');
        
        error.response.data = {
          detail: validationMessages,
          validation_errors: errorData.detail
        };
      }
      // Si es un objeto con estructura {type, loc, msg, input, url}
      else if (errorData.detail && typeof errorData.detail === 'object' && !Array.isArray(errorData.detail)) {
        const detail = errorData.detail;
        if (detail.msg || detail.type) {
          error.response.data = {
            detail: detail.msg || detail.type || 'Error de validación',
            validation_errors: [detail]
          };
        }
      }
    }

    // Endpoints públicos que no requieren token de autorización
    const isPublicEndpoint = isPublicApiPath(originalRequest.url);
    
    if (error.response?.status === 401 && !originalRequest._retry && !isPublicEndpoint) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const { access_token, refresh_token } = response.data;
          localStorage.setItem('access_token', access_token);
          localStorage.setItem('refresh_token', refresh_token);
          emitAuthTokensEvent(access_token, refresh_token);

          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        } else {
          clearSessionAndRedirect();
          return Promise.reject(new Error('No refresh token available'));
        }
      } catch (refreshError) {
        clearSessionAndRedirect();
        return Promise.reject(new Error('Token refresh failed'));
      }
    }

    if (error.response?.status === 401 && !isPublicEndpoint) {
      clearSessionAndRedirect();
      return Promise.reject(new Error('Unauthorized'));
    }

    return Promise.reject(error);
  }
);

export default api;


